/** Pins drawn into a time-series chart's ECharts option (TimeSeriesChart).
 *
 *  A point pin is a `markPoint` on its own series at the stored reading: a
 *  12 px white circle ringed in the series colour. A stretch is one extra
 *  line built from the stored points inside it (`pinOverlayPoints`), drawn
 *  wide and translucent behind its series: a copy of readings already on
 *  the chart, through the same gap rule, silent, and out of the legend and
 *  the tooltip, so it adds nothing a reader could take for data.
 *
 *  Pin mode stops a drag from panning (the inside dataZoom's
 *  `moveOnMouseMove`), so that the drag can pin a stretch; the wheel still
 *  zooms. The option given is never changed. */
import type { EChartsOption } from 'echarts'
import type { ChartPin, Series, SeriesPoint } from '../api/types'
import { pinOverlayPoints } from './pins'

type Gapped = (series: Series, points: SeriesPoint[]) => [number, number | null][]

interface LineSeries {
  name?: string
  yAxisIndex?: number
  step?: unknown
  itemStyle?: { color?: string }
  [key: string]: unknown
}

export const PIN_DOT_PX = 12

export function withPins(option: EChartsOption, series: Series[], pins: ChartPin[],
                         pinMode: boolean, gap: Gapped): EChartsOption {
  const lines = (option.series as LineSeries[] | undefined) ?? []
  const indexOf = new Map(series.map((s, i) => [s.parameter, i]))

  const drawn = lines.map((line, i) => {
    const points = pins.filter((p) => p.ts_end === null && p.value !== null
                                      && indexOf.get(p.parameter) === i)
    if (!points.length) return line
    const colour = line.itemStyle?.color
    return {
      ...line,
      markPoint: {
        silent: true,
        animation: false,
        symbol: 'circle',
        symbolSize: PIN_DOT_PX,
        label: { show: false },
        itemStyle: { color: '#FFFFFF', borderColor: colour, borderWidth: 2 },
        data: points.map((p) => ({ coord: [Date.parse(p.ts), p.value] })),
      },
    }
  })

  const overlays = pins.flatMap((pin) => {
    const i = indexOf.get(pin.parameter)
    if (pin.ts_end === null || i === undefined || !lines[i]) return []
    const parent = lines[i]!
    const colour = parent.itemStyle?.color
    return [{
      name: `pin ${pin.id}`,
      type: 'line' as const,
      yAxisIndex: parent.yAxisIndex,
      step: parent.step,
      silent: true,
      tooltip: { show: false },
      showSymbol: false,
      connectNulls: false,
      z: 1,
      lineStyle: { width: 9, opacity: 0.28, color: colour, cap: 'round' as const },
      itemStyle: { color: colour },
      emphasis: { disabled: true },
      data: gap(series[i]!, pinOverlayPoints(series[i]!, pin)),
    }]
  })

  const zooms = Array.isArray(option.dataZoom) ? option.dataZoom
    : option.dataZoom ? [option.dataZoom] : []
  const dataZoom = zooms.map((zoom) =>
    (zoom as { type?: string }).type === 'inside' ? { ...zoom, moveOnMouseMove: !pinMode } : zoom)

  return { ...option, dataZoom, series: [...drawn, ...overlays] as EChartsOption['series'] }
}

/** The option with the reader's legend choices kept. Every option replaces
 *  the last one whole (useECharts, notMerge), so a line hidden in the legend
 *  came back with each pin made or format changed until this carried the
 *  choice over. Names are the legend's own (the series names). */
export function withLegendSelection(option: EChartsOption,
                                    selected: Record<string, boolean>): EChartsOption {
  if (!Object.keys(selected).length) return option
  const legend = (option.legend ?? {}) as Record<string, unknown>
  return { ...option, legend: { ...legend, selected } as EChartsOption['legend'] }
}

/** The lines a click may pin: those on show. A line hidden in the legend is
 *  not on the chart, so nothing on it can be pinned. */
export function shownSeries(series: Series[], selected: Record<string, boolean>,
                            label: (s: Series) => string): Series[] {
  return series.filter((s) => selected[label(s)] !== false)
}

/** The pins on this chart's own lines. A gateway page shares one list of
 *  pins across its charts, and each chart places only its own. */
export function pinsForChart(pins: ChartPin[], series: Series[]): ChartPin[] {
  const drawn = new Set(series.map((s) => s.parameter))
  return pins.filter((pin) => drawn.has(pin.parameter))
}
